/*
Import a PEM encoded RSA private key, to use for RSA-PSS signing.
Takes a string containing the PEM encoded key, and returns a Promise
that will resolve to a CryptoKey representing the private key.
*/
export async function importPublicKey(pem) {
    // fetch the part of the PEM string between header and footer
    const pemHeader = '-----BEGIN PUBLIC KEY-----';
    const pemFooter = '-----END PUBLIC KEY-----';
    const pemContents = pem
      .replace(pemHeader, '')
      .replace(pemFooter, '').replace(/[\r\n]+/gm, '');
    // base64 decode the string to get the binary data
    const binaryDerString = window.atob(pemContents);
    // convert from a binary string to an ArrayBuffer
    const binaryDer = str2ab(binaryDerString);

    const key = await window.crypto.subtle.importKey(
      'spki',
      binaryDer,
      {
        name: 'RSA-OAEP',
        hash: 'SHA-256',
      },
      true,
      ['encrypt'],
    );
    return key;
}

/*
Convert a string into an ArrayBuffer
from https://developers.google.com/web/updates/2012/06/How-to-convert-ArrayBuffer-to-and-from-String
*/
export function str2ab(str) {
    const buf = new ArrayBuffer(str.length);
    const bufView = new Uint8Array(buf);
    for (let i = 0, strLen = str.length; i < strLen; i++) {
      bufView[i] = str.charCodeAt(i);
    }
    return buf;
}


export function ab2str(buffer: ArrayBuffer): string {
    const bufView = new Uint8Array(buffer);
    let str = '';
    for (let i = 0; i < bufView.length; i++) {
      str += String.fromCharCode(bufView[i]);
    }
    return str;
}
export function arrayBufferToBase64(buffer: ArrayBuffer){
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
}
export function base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binaryString = atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
}

/**
 * Generates a cryptographically secure random integer between 0 and (range - 1).
 * @param range The upper bound (exclusive) for the random number. Defaults to 100.
 * @returns A random integer.
 * @throws Error if range is not a positive integer or if crypto API is unavailable.
 */
export function getCryptoRandomInt(range: number = 10000): number {
    if (range <= 0 || !Number.isInteger(range)) {
      throw new Error('Range must be a positive integer');
    }

    const array = new Uint16Array(1);
    const max = Math.floor(0xFFFF / range) * range;
    let randomValue: number;

    // Browser crypto
    if (typeof window !== 'undefined' && window.crypto && typeof window.crypto.getRandomValues === 'function') {
      do {
        window.crypto.getRandomValues(array);
        randomValue = array[0];
      } while (randomValue >= max);
      return randomValue % range;
    }

    if (typeof require === 'function') {
      try {
      // eslint-disable-next-line @typescript-eslint/no-var-requires
        const nodeCrypto = require('crypto');
        const webcrypto = nodeCrypto.webcrypto;
        if (webcrypto && typeof webcrypto.getRandomValues === 'function') {
          do {
            webcrypto.getRandomValues(array);
            randomValue = array[0];
          } while (randomValue >= max);
          return randomValue % range;
        }
      } catch (e) {
        throw new Error('No secure require crypto API available');
      }
    }

    throw new Error('No secure crypto API available');
}